import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { categories } from "@/mocks/categories";
import { articlePreviews } from "@/mocks/articles";
import Header from "@/components/feature/Header";

export default function Home() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".category-dropdown")) setDropdownOpen(false);
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[560px] md:min-h-[680px] flex items-center justify-center overflow-hidden pt-20 md:pt-24">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20streetwear%20fashion%20editorial%20with%20colorful%20sneakers%20hoodies%20and%20clothing%20arranged%20in%20dynamic%20layered%20composition%2C%20bold%20urban%20aesthetic%2C%20energetic%20mood%2C%20rich%20warm%20colors%2C%20soft%20studio%20lighting%2C%20premium%20product%20photography&width=1920&height=1080&seq=hero-bg&orientation=landscape&nocache=true"
            alt="AllChinaBuy Spreadsheet Fashion Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center w-full">
          <span className="inline-block text-sm md:text-base font-medium tracking-widest text-white/85 uppercase mb-4">
            {t("hero.eyebrow")}
          </span>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {t("hero.h1")}
          </h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
            {t("hero.subtitle")}
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-6 py-3.5 rounded-md font-medium text-white transition-all duration-200 hover:scale-105 bg-primary-500 hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-external-link-line mr-2" />
              {t("hero.cta1")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-6 py-3.5 rounded-md font-medium transition-all duration-200 hover:scale-105 border border-white/40 text-white hover:bg-white/10 whitespace-nowrap"
            >
              {t("hero.cta2")}
            </a>
          </div>

          <p className="text-sm text-white/70 flex items-center justify-center gap-1.5">
            <i className="ri-shield-check-line" />
            {t("hero.trust")}
          </p>
        </div>
      </section>

      {/* Category Dropdown */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t("categories.select")}
            </h2>
          </div>

          <div className="category-dropdown relative max-w-lg mx-auto">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-4 rounded-lg bg-white border-2 border-background-200/70 hover:border-primary-300 transition-all text-left"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-primary-500 text-lg" />
                <span className="font-medium text-foreground-800">
                  {t("categories.select")}
                </span>
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-500 text-lg transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-background-200/70 z-40 max-h-[400px] overflow-y-auto">
                {categories.map((cat, idx) => (
                  <a
                    key={idx}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-background-50 transition-colors border-b border-background-100/50 last:border-b-0"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-50 text-primary-500 shrink-0">
                      <i className={`${cat.icon} text-sm`} />
                    </span>
                    <span className="text-sm font-medium text-foreground-800">
                      {t(cat.nameKey)}
                    </span>
                    <i className="ri-arrow-right-up-line ml-auto text-foreground-400 text-sm" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Category Buttons Grid */}
      <section className="section-padding bg-background-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-8">
            {t("categories.title")}
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat, idx) => (
              <a
                key={idx}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col rounded-lg overflow-hidden bg-white border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={cat.image}
                    alt={t(cat.nameKey)}
                    title={`${t(cat.nameKey)} AllChinaBuy`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-3 flex items-center gap-2 border-t border-background-100/50">
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-50 text-primary-500 shrink-0">
                    <i className={`${cat.icon} text-sm`} />
                  </span>
                  <span className="text-sm font-medium text-foreground-800 leading-tight group-hover:text-primary-500 transition-colors">
                    {t(cat.nameKey)}
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 text-primary-500 font-medium hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              {t("categories.viewAll")}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20pastel%20abstract%20watercolor%20texture%20in%20warm%20cream%20and%20sage%20green%20tones%2C%20gentle%20organic%20flowing%20shapes%2C%20minimalist%20elegant%20background%2C%20airy%20light%20aesthetic&width=1200&height=800&seq=whatis-bg&orientation=landscape&nocache=true"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-4xl mx-auto relative">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                {t("whatis.h2")}
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">
                {t("whatis.text")}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary inline-flex"
              >
                {t("whatis.cta")}
                <i className="ri-arrow-right-line ml-2" />
              </a>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20laptop%20displaying%20colorful%20spreadsheet%20with%20charts%20and%20graphs%2C%20clean%20bright%20desk%20with%20small%20plant%20and%20coffee%20cup%2C%20fresh%20airy%20workspace%2C%20soft%20natural%20light%2C%20minimal%20aesthetic&width=800&height=600&seq=whatis-img&orientation=landscape&nocache=true"
                alt="AllChinaBuy Spreadsheet Guide"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Use This Resource */}
      <section className="section-padding bg-background-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
            {t("whyuse.h2")}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { key: "whyuse.card1", icon: "ri-search-2-line" },
              { key: "whyuse.card2", icon: "ri-bar-chart-box-line" },
              { key: "whyuse.card3", icon: "ri-camera-3-line" },
              { key: "whyuse.card4", icon: "ri-truck-line" },
              { key: "whyuse.card5", icon: "ri-shield-check-line" },
            ].map((card, idx) => (
              <div key={idx} className="card-elevated p-5 md:p-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-4">
                  <i className={`${card.icon} text-lg`} />
                </div>
                <h3 className="font-semibold text-foreground-900 mb-2">
                  {t(`${card.key}.title`)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(`${card.key}.text`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
            {t("articles.h2")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {articlePreviews.map((article, idx) => (
              <button
                key={idx}
                onClick={() => { navigate(`/${article.slug}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="group text-left rounded-lg overflow-hidden bg-white border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={article.image}
                    alt={t(article.titleKey)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground-700">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-xs text-foreground-400 mb-2">{t(article.dateKey)}</p>
                  <h3 className="font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {t(article.titleKey)}
                  </h3>
                  <p className="text-sm text-foreground-600 line-clamp-2 mb-4">
                    {t(article.summaryKey)}
                  </p>
                  <span className="inline-flex items-center text-sm font-medium text-primary-500">
                    {t("articles.readMore")}
                    <i className="ri-arrow-right-line ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-padding bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-5">
            <i className="ri-shield-check-line text-xl" />
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {t("trust.h2")}
          </h2>
          <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-6">
            {t("trust.text")}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-secondary inline-flex"
          >
            {t("trust.cta")}
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20fashion%20product%20collage%20with%20diverse%20clothing%20accessories%20and%20shoes%20arranged%20in%20artistic%20grid%20pattern%2C%20colorful%20modern%20editorial%20style%2C%20soft%20natural%20lighting%2C%20warm%20vibrant%20tones%2C%20clean%20composition&width=1920&height=800&seq=cta-bg&orientation=landscape&nocache=true"
            alt="AllChinaBuy Fashion Products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/50 to-black/60" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-display text-2xl md:text-4xl font-bold text-white mb-4">
            {t("finalcta.h2")}
          </h2>
          <p className="text-white/80 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t("finalcta.text")}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-6 py-3.5 rounded-md font-medium text-white transition-all duration-200 hover:scale-105 bg-primary-500 hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-external-link-line mr-2" />
              {t("finalcta.cta1")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-6 py-3.5 rounded-md font-medium transition-all duration-200 hover:scale-105 border border-white/40 text-white hover:bg-white/10 whitespace-nowrap"
            >
              {t("finalcta.cta2")}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground-950 text-white/80 py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div>
              <h3 className="text-white font-semibold mb-3">AllChinaBuy</h3>
              <p className="text-sm text-white/60 leading-relaxed">
                {t("footer.disclaimer")}
              </p>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">{t("nav.spreadsheet")}</h4>
              <div className="space-y-2">
                <button onClick={() => navigate("/")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">{t("nav.home")}</button>
                <button onClick={() => navigate("/allchinabuy-spreadsheet")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">AllChinaBuy Spreadsheet</button>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">{t("nav.guide")}</h4>
              <div className="space-y-2">
                <button onClick={() => navigate("/allchinabuy-buying-guide")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">AllChinaBuy Buying Guide</button>
                <button onClick={() => navigate("/allchinabuy-safety-guide")} className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">AllChinaBuy Safety Guide</button>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium text-sm mb-3">{t("footer.contact")}</h4>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="block text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">kaspreadsheet.com</a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center text-sm text-white/50">
            {t("footer.copyright")}
          </div>
        </div>
      </footer>
    </div>
  );
}